"use client";

import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React, { Suspense } from "react";

import { ErrorView } from "@/components/error-view";
import { Oriented } from "@/data/constants/oriented";
import { useHistoricalRating } from "@/features/analysis/historical-rating/services/historical-rating";
import { useLang } from "@/hooks/use-lang";
import { useUpdateQuery } from "@/hooks/use-update-query";
import {
  DateOption,
  getDateOptionsToTextMap,
  getDisplayDateOptions,
  getTextToDateOptionMap,
  yearDateOptions,
} from "@/utils/date-options";

import { Select } from "ui/core/select";
import { ChartSkeletons } from "ui/widget/skeletons/Skeletons";
import { ToggleButtons } from "ui/widget/toggle-buttons";

type Props = {
  symbol: string;
  oriented: Oriented;
};

const HistoricalRatingChart = dynamic(
  () =>
    import("./HistoricalRatingChart").then(
      (mod) => mod.MemoHistoricalRatingChart
    ),
  { ssr: false }
);

/**
 * TODO: This component can be reduced to one client and one server component
 *
 * 1. remove this file and related react-query
 * 2. move `date-buttons` and `historical-chart` to `page.tsx`
 * 3. you can update url state in `date-buttons` (client component)
 * 4. you can fetch data and slice data using url state in `historical-chart` (server component)
 *
 * since in nextJS 13.5.5, need to make this component as client component to prevent flash issue when changing date option, the refactor will be done later
 **/

export const HistoricalRatingComponent = React.memo<Props>(
  ({ symbol, oriented }) => {
    const { t } = useLang();
    const { updateQuery } = useUpdateQuery();

    const displayOptions = getDisplayDateOptions({
      t,
      options: yearDateOptions,
    });
    const dateOptionToTextMap = getDateOptionsToTextMap({ t });
    const textToDateOptionMap = getTextToDateOptionMap({ t });

    const {
      data: historicalRatings,
      isLoading,
      isError,
    } = useHistoricalRating({
      symbol,
      oriented,
    });

    const searchParams = useSearchParams();
    const dateOption = (searchParams.get("historical-rating-date-option") ??
      "5Y") as DateOption;

    if (isLoading) return <LoadingView />;
    if (isError)
      return (
        <ErrorView errorType="error" className="min-h-[300px]">
          {t.error.serverError}
        </ErrorView>
      );
    if (historicalRatings.length === 0)
      return (
        <ErrorView errorType="empty" className="min-h-[300px]">
          {t.error.noData}
        </ErrorView>
      );

    return (
      <div className="pb-5">
        <div className="text-end">
          <ToggleButtons
            className="gap-2.5"
            itemClassName="tracking-widest hidden @lg:inline-block bg-transparent rounded-lg text-zinc-500 text-sm font-bold hover:text-zinc-800"
            selectedItemClassName="bg-sky-100 shadow-none text-sky-600 hover:text-inset"
            options={displayOptions.map((option) => ({ value: option }))}
            value={dateOptionToTextMap[dateOption] || dateOption}
            onChange={(value) => {
              updateQuery({
                name: "historical-rating-date-option",
                value: textToDateOptionMap[value] || value,
              });
            }}
            fullWidth={false}
            aria-label="historical rating date option"
          />
          <Select
            value={dateOptionToTextMap[dateOption] || dateOption}
            options={displayOptions}
            className="inline-block max-w-[180px] @lg:hidden"
            onChange={(e) => {
              updateQuery({
                name: "historical-rating-date-option",
                value: textToDateOptionMap[e.target.value] || e.target.value,
              });
            }}
            aria-label="historical rating date select"
          />
        </div>
        <Suspense>
          <HistoricalRatingChart
            historicalRatings={historicalRatings}
            dateOption={dateOption}
          />
        </Suspense>
      </div>
    );
  }
);
HistoricalRatingComponent.displayName = "HistoricalRatingComponent";

const LoadingView = () => <ChartSkeletons className="my-12" />;
